// useCalendars — list/create/rename/delete of the user's calendars,
// plus membership management. Separate from useCalendar (which owns
// events) so the calendar list survives view changes and re-fetches
// only when calendars themselves mutate.

import { useCallback, useEffect, useState } from "preact/hooks";
import { api, type Calendar, type CalendarMember, type CalendarShareableUser } from "./rpc";

export interface UseCalendars {
  calendars: Calendar[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  create: (args: { name: string; color?: string | null }) => Promise<Calendar | null>;
  rename: (id: string, name: string) => Promise<Calendar | null>;
  remove: (id: string) => Promise<boolean>;
  listMembers: (id: string) => Promise<CalendarMember[]>;
  addMember: (id: string, targetUserId: string, role?: "owner" | "editor" | "viewer") => Promise<boolean>;
  removeMember: (id: string, targetUserId: string) => Promise<boolean>;
  listShareableUsers: () => Promise<CalendarShareableUser[]>;
  setHousehold: (id: string, householdId: number | null) => Promise<Calendar | null>;
}

export function useCalendars(): UseCalendars {
  const [calendars, setCalendars] = useState<Calendar[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await api.calendarListMyCalendars();
      setCalendars(list);
    } catch (e: any) {
      setError(e?.message || "failed to load calendars");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const create = useCallback(async (args: { name: string; color?: string | null }) => {
    try {
      const cal = await api.calendarCreateCalendar(args);
      setCalendars((prev) => [...prev, cal].sort((a, b) => a.name.localeCompare(b.name)));
      return cal;
    } catch (e: any) {
      setError(e?.message || "create failed");
      return null;
    }
  }, []);

  const rename = useCallback(async (id: string, name: string) => {
    try {
      const cal = await api.calendarRenameCalendar({ id, name });
      setCalendars((prev) => prev.map((c) => (c.id === id ? cal : c)));
      return cal;
    } catch (e: any) {
      setError(e?.message || "rename failed");
      return null;
    }
  }, []);

  const remove = useCallback(async (id: string) => {
    try {
      await api.calendarDeleteCalendar(id);
      setCalendars((prev) => prev.filter((c) => c.id !== id));
      return true;
    } catch (e: any) {
      setError(e?.message || "delete failed");
      return false;
    }
  }, []);

  const listMembers = useCallback(async (id: string) => {
    try { return await api.calendarListMembers(id); }
    catch (e: any) { setError(e?.message || "list members failed"); return []; }
  }, []);

  const addMember = useCallback(async (id: string, targetUserId: string, role?: "owner" | "editor" | "viewer") => {
    try { await api.calendarAddMember({ id, targetUserId, role }); return true; }
    catch (e: any) { setError(e?.message || "add member failed"); return false; }
  }, []);

  const removeMember = useCallback(async (id: string, targetUserId: string) => {
    try { await api.calendarRemoveMember({ id, targetUserId }); return true; }
    catch (e: any) { setError(e?.message || "remove member failed"); return false; }
  }, []);

  const listShareableUsers = useCallback(async () => {
    try { return await api.calendarListShareableUsers(); }
    catch { return []; }
  }, []);

  const setHousehold = useCallback(async (id: string, householdId: number | null) => {
    try {
      const cal = await api.calendarSetHousehold({ id, householdId });
      setCalendars((prev) => prev.map((c) => (c.id === id ? cal : c)));
      return cal;
    } catch (e: any) {
      setError(e?.message || "set household failed");
      return null;
    }
  }, []);

  return {
    calendars, loading, error, refresh,
    create, rename, remove,
    listMembers, addMember, removeMember,
    listShareableUsers, setHousehold,
  };
}
